import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import documentService from "@/services/sign/services/document.service";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation } from "vuex-module-decorators";
import {
  SignCreateDocumentDtoGen,
  SignDocumentControllerFindAllParamsGen,
  SignDocumentViewModelGen,
  SignUpdateDocumentDtoGen,
  SignUpdateDocumentTokenDtoGen
} from "./../../services/sign/v1/data-contracts";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";

@Module({
  dynamic: true,
  namespaced: true,
  name: "document",
  store
})
export class DocumentPaginationStore extends BasePagination<
  SignDocumentViewModelGen,
  SignDocumentControllerFindAllParamsGen
> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 100;
  protected _totalPages = 0;
  protected _paginationList: SignDocumentViewModelGen[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = true;
  filterOptions: IPaginationFilterListElement[] = [{ key: "partnerId", type: FilterTypes.OBJECT_ID }];

  private _idMap = new Map<string, SignDocumentViewModelGen>();
  protected _documentMapConfig = { id: this._idMap };

  @Action
  protected async loadDocuments(query: SignDocumentControllerFindAllParamsGen): Promise<any> {
    return documentService.getAllForPartner(query);
  }

  private _signDocument: SignDocumentViewModelGen = {} as any;

  get signDocument() {
    return this._signDocument;
  }

  get documents() {
    return this._paginationList;
  }

  @Action
  async getAll(data: { partnerId: string }) {
    await this.fetchAllFromBeginning({
      partnerId: data.partnerId
    });

    this.context.commit("mutateDocuments", this._paginationList);
  }

  @Mutation
  mutateSignDocument(document: SignDocumentViewModelGen) {
    this._signDocument = document;
  }

  @Mutation
  mutateDocuments(documents: SignDocumentViewModelGen[]) {
    this._paginationList = documents;
  }

  @Action
  async create(data: { partnerId: string; data: SignCreateDocumentDtoGen }) {
    const res = await documentService.create(data.partnerId, data.data);

    this.context.commit("mutateSignDocument", res);
    this.addToList(res);

    return res;
  }

  @Action
  async delete(data: { partnerId: string; documentId: string }) {
    const res = await documentService.removeForPartner(data.partnerId, data.documentId);

    this.context.commit("mutateSignDocument", res);
    this.removeInList(res);

    return res;
  }

  @Action
  async getOne(data: { partnerId: string; documentId: string }) {
    const res = await documentService.getOneForPartner(data.partnerId, data.documentId);

    this.context.commit("mutateSignDocument", res);
    this.addToList(res);

    return res;
  }

  /**
   * Does not mutate store
   * @param data
   * @returns
   */
  @Action
  async getOneLocal(data: { partnerId: string; documentId: string }) {
    const res = await documentService.getOneForPartner(data.partnerId, data.documentId);

    return res;
  }

  @Action
  async update(data: { partnerId: string; documentId: string; data: SignUpdateDocumentDtoGen }) {
    const res = await documentService.updateForPartner(data.partnerId, data.documentId, data.data);

    this.context.commit("mutateSignDocument", res);
    this.replaceInList(res);

    return res;
  }

  @Action
  async updateDocumentTokens({
    partnerId,
    documentId,
    dto
  }: {
    partnerId: string;
    documentId: string;
    dto: SignUpdateDocumentTokenDtoGen;
  }): Promise<SignDocumentViewModelGen> {
    const document = await documentService.updateDocumentTokens(partnerId, documentId, dto);

    this.context.commit("mutateSignDocument", document);
    this.replaceInList(document);

    return document;
  }
}

export const DocumentModule = getModule(DocumentPaginationStore);
